import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Link from '@mui/material/Link'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useTranslation } from 'react-i18next'
import { alpha } from '@mui/material/styles'

import type { SeasonalActivityDto } from '../../types/api'
import { seasonColors } from '../../theme/theme'

/**
 * One activity.
 *
 * The emoji comes from the database, not from a lookup table here: the owner picks it in the
 * admin console when they add "ice fishing" or "mushroom foraging". That is why the field is
 * a string rather than an icon-name enum, and why adding a new activity never needs a deploy.
 */
export function ActivityCard({ activity }: { activity: SeasonalActivityDto }) {
  const { t } = useTranslation()
  const accent = seasonColors[activity.season]

  const duration = activity.durationMinutes
    ? activity.durationMinutes >= 120
      ? t('activities.durationHours', { count: Math.round(activity.durationMinutes / 60) })
      : t('activities.duration', { count: activity.durationMinutes })
    : null

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          height: 6,
          bgcolor: accent,
        }}
      />
      <CardContent sx={{ p: 2.75, display: 'flex', flexDirection: 'column', gap: 1.25, flex: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            aria-hidden
            sx={{
              fontSize: '1.9rem',
              lineHeight: 1,
              width: 52,
              height: 52,
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              borderRadius: 3,
              bgcolor: alpha(accent, 0.12),
            }}
          >
            {activity.icon ?? '📍'}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 0.25 }}>
              {activity.title}
            </Typography>
            <Typography variant="caption" sx={{ color: accent, fontWeight: 700 }}>
              {t(`activities.${activity.season}`)}
            </Typography>
          </Box>
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {activity.summary}
        </Typography>

        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75, pt: 0.5 }}>
          {duration && <Chip size="small" variant="outlined" label={`⏱ ${duration}`} />}
          {activity.difficulty && (
            <Chip size="small" variant="outlined" label={`💪 ${activity.difficulty}`} />
          )}
          {activity.distanceKm && <Chip size="small" variant="outlined" label={`📍 ${activity.distanceKm}`} />}
          {activity.bookingRequired ? (
            <Chip size="small" color="warning" variant="outlined" label={`📅 ${t('activities.bookingRequired')}`} />
          ) : (
            <Chip size="small" color="success" variant="outlined" label={`✅ ${t('activities.included')}`} />
          )}
        </Stack>

        {activity.priceInfo && (
          <Typography variant="caption" color="text.secondary">
            {activity.priceInfo}
          </Typography>
        )}

        {activity.providerName && activity.providerUrl && (
          <Link
            href={activity.providerUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="caption"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            {t('activities.provider', { name: activity.providerName })}
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </Link>
        )}
      </CardContent>
    </Card>
  )
}
